<script setup>import { ref as _ref } from 'vue';

  import ConfirmDialog from "../shared/confirm-dialog.vue";
  import WrapWithHint from "../wrap-with-hint.vue";
  import { vFieldErrors } from "~/jose_modern/field_with_errors";
  import { useLineItemRow } from "./use-line-item-row.js";

  const props = defineProps({
    item: { type: Object, default: null },
    itemCombobox: { type: [Object, Function], required: true },
    comboboxProps: { type: Object, default: () => ({}) },
    mapSelection: { type: Function, required: true },
    api: { type: Object, required: true },
    labels: { type: Object, default: () => ({}) },
    excludedItemIds: { type: Array, default: () => [] },
    duplicate: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
  });

  const emit = defineEmits(["created", "remove", "dirty-changed", "item-changed"]);

  const itemSearchRef = _ref(null);
  const quantityInput = _ref(null);

  const {
    labels,
    itemId,
    name,
    itemCustomName,
    displayName,
    quantity,
    unit,
    units,
    saving,
    errored,
    showConfirmRemove,
    rowEl,
    isDraft,
    quantityError,
    excludedItemIds,
    selectItem,
    clearSelection,
    onRowFocusOut,
    onEnter,
    removeItem,
    confirmRemove,
  } = useLineItemRow(props, emit, {
    canSave: () => !props.duplicate,
    focusItem: () => itemSearchRef.value?.focus(),
    focusQuantity: () => quantityInput.value?.focus(),
  });
</script>

<template>
  <div
    ref="rowEl"
    class="line-items-row"
    :class="{ draft: isDraft, errored }"
    @focusout="onRowFocusOut"
    @keydown.enter="onEnter"
  >
    <div class="is-relative">
      <div class="field">
        <label class="label hidden-desktop">Item</label>
        <div v-if="itemId" class="field has-addons mb-0">
          <WrapWithHint
            v-if="itemCustomName"
            tag="div"
            class="control is-expanded"
            :content="name"
            style="display: flex"
          >
            <input class="input" type="text" :value="displayName" disabled style="flex: 1" />
          </WrapWithHint>
          <div v-else class="control is-expanded">
            <input class="input" type="text" :value="displayName" disabled />
          </div>
          <div class="control">
            <button
              class="button"
              :class="{ 'is-loading': saving }"
              :disabled="saving"
              @click="clearSelection"
            >
              <span class="icon is-small"><i class="fas fa-edit"></i></span>
            </button>
          </div>
        </div>
        <template v-else>
          <component
            :is="itemCombobox"
            ref="itemSearchRef"
            v-bind="comboboxProps"
            :excluded-item-ids="excludedItemIds"
            :autofocus="autofocus"
            @select="selectItem"
          />
        </template>
      </div>
    </div>
    <div>
      <div class="field qty mb-0">
        <label class="label hidden-desktop">Quantity</label>
        <div class="field has-addons">
          <div class="control">
            <input
              ref="quantityInput"
              v-model="quantity"
              v-field-errors
              class="input"
              :class="{ 'is-danger': quantityError }"
              :disabled="saving"
              type="number"
              placeholder="Qty"
              :data-errors="
                quantityError ? JSON.stringify({ quantity: [quantityError] }) : undefined
              "
            />
          </div>
          <div class="control">
            <div class="select unit">
              <select v-model="unit" :disabled="saving || !units.length">
                <option v-if="!units.length" value="" disabled>Unit</option>
                <option v-for="u in units" :key="u" :value="u">{{ u }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="remove">
      <button class="button is-ghost remove-btn is-danger" :disabled="saving" @click="removeItem">
        <span class="icon is-small"><i class="fas fa-trash-can"></i></span>
      </button>
    </div>
  </div>

  <ConfirmDialog
    v-model="showConfirmRemove"
    :title="labels.removeTitle"
    message=""
    confirm-text="Remove"
    :is-loading="saving"
    @confirm="confirmRemove"
  >
    <p>
      {{ labels.removeMessage }}<br />
      <b>{{ name }}</b>
    </p>
  </ConfirmDialog>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";
  @import "./line-items";

  .line-items-row {
    @include line-items-columns;

    border-bottom: 1px solid $border-light;

    @media screen and (min-width: $line-items-collapse + 1) {
      .label {
        display: none;
      }
    }

    @media screen and (max-width: $line-items-collapse) {
      @include line-items-card;

      grid-template-columns: 1fr;
    }
  }

  .draft {
    border-left: 3px solid $secondary;
  }

  .errored {
    border-left: 3px solid $danger;
  }

  .qty {
    .input {
      width: 12ch;
    }
    .select {
      max-width: 20ch;
    }
  }

  .remove {
    display: flex;
    justify-content: flex-end;

    .button {
      align-self: end;
    }
  }

  .remove-btn:not([disabled]) {
    opacity: 0;
    transition: opacity 0.15s;
  }

  .line-items-row:hover .remove-btn,
  .remove-btn:focus {
    &:not([disabled]) {
      opacity: 1;
    }
  }

  .unit select {
    width: 20ch;
  }

  @include touch {
    .remove-btn:not([disabled]) {
      opacity: 1;
    }

    .qty .select,
    .unit select {
      max-width: 100%;
      width: 100%;
    }

    .line-items-row.draft {
      border-left: 3px solid $secondary;
    }

    .line-items-row.errored {
      border-left: 3px solid $danger;
    }
  }
</style>
